import { Chip, ChipPicker, ListItem } from '@tableau/widgets';
import * as React from 'react';

import { Messages } from '../../gen-src/Localize';

export const maxTagLength = 255;

/** Props for the Tags Editor. */
export type TagsEditorProps = React.HTMLAttributes<HTMLDivElement> & {
  /** The current state of the Tags Editor. */
  readonly editorState: TagsEditorState;

  /** Callback to update the editor state in the parent. This will be called by TagsEditor when the state needs to change.  */
  readonly setEditorState: React.Dispatch<React.SetStateAction<TagsEditorState>>;

  /** Async callback to search for matching tags to populate the editor's dropdown. */
  readonly searchMatchingTags: (partialText: string) => Promise<ReadonlyArray<string>>;

  /**
   * When this boolean is true, the chip picker will become disabled.
   * Its intended usage is to disable the editor while the tags are being saved.
  */
  readonly disableEditor: boolean;

  /** Optional prop to show a custom label on the ChipPicker. */
  readonly label?: string;
};

/** The state of the Tags Editor. This is intended to be stored by a parent component. */
export type TagsEditorState = {
  /** Text entered in the editor that isn't part of an existing tag. */
  readonly partialText: string | null;

  /** Matching tags to currently populate in the "suggestions" dropdown. */
  readonly suggestions: ListItem[];

  /** The list of tag chips in the editor. */
  readonly tags: ReadonlyArray<string>;
};

const TagsEditorTestId = 'tags-editor';


/**
 * This component wraps the ChipPicker widget with functionality specific to editing tags on
 * Tableau Server content items. It does not include save/cancel logic (which can be provided,
 * depending on the UX situation, with a helper such as `ReactConfirmActionDialog` or `useInlineEditor`),
 * nor does it provide API connectivity to get/save the tags (this is also instantiation-specific).
 *
 * This is a controlled component, hence state must be managed by the parent. This component was
 * designed to work with the state management logic of either `ReactConfirmActionDialog` or `useInlineEditor`.
 * In common usage, the parent can simply store the state updates provided by `setEditorState` with
 * no additional processing (i.e. pass the updates directly to the dialog's state handler).
 *
 * @param props See documentation for TagsEditorProps
 */
export function TagsEditor(propsDestructureBeforeUsing: TagsEditorProps): JSX.Element {

  const {
    setEditorState,
    editorState,
    label,
    disableEditor,
    searchMatchingTags,
    ...divProps
  } = propsDestructureBeforeUsing;

  const onFilterItems = React.useCallback(
    (value: string) => setEditorState(
      prevState => ({ ...prevState, partialText: value })
    ),
    [setEditorState]
  );

  const onValueAdd = React.useCallback((value: string, index: number) => {
    setEditorState(prevState => {
      if (prevState.tags.find(t => t === value) !== undefined) {
        return prevState;
      } else {
        const updated = [...prevState.tags];
        updated.splice(index, 0, value);

        return { ...prevState, tags: updated };
      }
    });
  }, [setEditorState]);

  const onValuesChange = React.useCallback((selectedValues: ReadonlyArray<string>) => {
    setEditorState(prevState => ({ ...prevState, tags: selectedValues }));
  }, [setEditorState]);

  const onChangeCapture = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const clean = value.replace(/[",]/g, '');

    event.target.value = clean.length > maxTagLength ? clean.slice(0, maxTagLength) : clean;
  }, []);

  // Change handler for partialText. Calls the API to get the tag suggestions.
  React.useEffect(
    () => {
      const value = editorState.partialText?.trim();

      if (!value) {
        setEditorState(prevState => ({ ...prevState, suggestions: [] }));
        return;
      } else {
        // this allows the input value to show up in the dropdown immediately,
        // but along with previously fetched suggestions to reduce flickering
        setEditorState(prevState => ({
          ...prevState,
          suggestions: [ { value, disabled: false }, ...prevState.suggestions.slice(1) ]
        }));
      }

      let latestQuery = true;
      searchMatchingTags(value).then(matchingTags => {
        if (latestQuery) {
          const suggestions = suggestionsToListItems(value, matchingTags);
          setEditorState(prevState => ({ ...prevState, suggestions }));
        }
      }).catch(_ => { return; });

      return () => {
        latestQuery = false;
      };
    },

    // This deps array was noncompliant before we enabled the lint rule.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editorState.partialText]
  );


  return (
    <ChipPicker
      {...divProps}
      label={label ?? ''}
      disabled={disableEditor}
      testId={TagsEditorTestId}
      listItems={editorState.suggestions}
      placeholder={Messages.AddATag()}
      onValueAdd={onValueAdd}
      onValuesChange={onValuesChange}
      onFilterItems={onFilterItems}
      onChangeCapture={onChangeCapture}
    >
      {
        editorState?.tags.map((value: string) => (
          <Chip key={value} tooltip={value} value={value}>{value}</Chip>
        ))
      }
    </ChipPicker>
  );
}


function suggestionsToListItems(search: string, suggestions: ReadonlyArray<string>): ListItem[] {
  const mapped = suggestions.map(t => ({ value: t, disabled: false }));
  const match = mapped.findIndex(t => t.value === search);

  if (match > -1) {
    mapped.splice(match, 1);
  }

  mapped.unshift({ value: search, disabled: false });

  return mapped;
}

/**
 * For most content items, we have separate APIs for adding and removing tags. This helper function
 * takes the diff between the old and the new tags lists to figure out which ones need to be added or removed.
 *
 * It also encloses each tag in an extra set of quotation marks, which is required for the API to properly
 * handle tags containing spaces.
 */
export function getTagListsForVizportalApi(
  params: { oldTags: ReadonlyArray<string>, newTags: ReadonlyArray<string> }
): { tagsToAdd: ReadonlyArray<string>, tagsToRemove: ReadonlyArray<string> } {
  const { oldTags, newTags } = params;

  const tagsToAdd = newTags.filter(tag => oldTags.indexOf(tag) === -1).map(tag => `"${tag}"`);
  const tagsToRemove = oldTags.filter(tag => newTags.indexOf(tag) === -1).map(tag => `"${tag}"`);

  return { tagsToAdd, tagsToRemove };
}
